import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { useCreateAccount, useUpdateAccount, useChartOfAccounts } from '@/hooks/queries/useAccounting';
import { useAuthStore } from '@/stores/authStore';
import { toast } from 'sonner';
import type { ChartOfAccount, AccountClass, AccountType, AccountNature } from '@/types/accounting';

const accountSchema = z.object({
  account_code: z.string().min(1, 'Code compte requis').regex(/^\d+$/, 'Numéros uniquement'),
  account_name: z.string().min(1, 'Nom du compte requis'),
  account_name_ar: z.string().optional(),
  parent_code: z.string().optional(),
  account_class: z.coerce.number().min(1).max(9),
  account_type: z.enum(['asset', 'liability', 'equity', 'revenue', 'expense']),
  nature: z.enum(['debit', 'credit', 'both']).optional(),
  opening_balance: z.coerce.number().default(0),
  description: z.string().optional(),
});

type AccountFormData = z.infer<typeof accountSchema>;

interface AccountFormProps {
  open: boolean;
  onClose: () => void;
  account?: ChartOfAccount;
}

const ACCOUNT_CLASSES = [
  { value: 1, label: 'Classe 1 - Comptes de capitaux / حسابات رأس المال' },
  { value: 2, label: 'Classe 2 - Immobilisations / الأصول الثابتة' },
  { value: 3, label: 'Classe 3 - Stocks / المخزون' },
  { value: 4, label: 'Classe 4 - Comptes de tiers / حسابات الغير' },
  { value: 5, label: 'Classe 5 - Comptes financiers / الحسابات المالية' },
  { value: 6, label: 'Classe 6 - Comptes de charges / حسابات الأعباء' },
  { value: 7, label: 'Classe 7 - Comptes de produits / حسابات المنتجات' },
  { value: 8, label: 'Classe 8 - Comptes spéciaux / الحسابات الخاصة' },
  { value: 9, label: 'Classe 9 - Comptes analytiques / الحسابات التحليلية' },
];

const ACCOUNT_TYPES = [
  { value: 'asset', label: 'Actif / أصول' },
  { value: 'liability', label: 'Passif / خصوم' },
  { value: 'equity', label: 'Capitaux propres / حقوق الملكية' },
  { value: 'revenue', label: 'Produits / إيرادات' },
  { value: 'expense', label: 'Charges / مصاريف' },
];

const NATURES = [
  { value: 'debit', label: 'Débiteur / مدين' },
  { value: 'credit', label: 'Créditeur / دائن' },
  { value: 'both', label: 'Les deux / كلاهما' },
];

export function AccountForm({ open, onClose, account }: AccountFormProps) {
  const { company } = useAuthStore();
  const createAccount = useCreateAccount();
  const updateAccount = useUpdateAccount();

  const form = useForm<AccountFormData>({
    resolver: zodResolver(accountSchema),
    defaultValues: account
      ? {
          account_code: account.account_code,
          account_name: account.account_name,
          account_name_ar: account.account_name_ar || '',
          parent_code: account.parent_code || '',
          account_class: account.account_class,
          account_type: account.account_type,
          nature: account.nature || undefined,
          opening_balance: account.opening_balance,
          description: account.description || '',
        }
      : {
          account_code: '',
          account_name: '',
          account_name_ar: '',
          parent_code: '',
          account_class: 4,
          account_type: 'asset',
          nature: 'debit',
          opening_balance: 0,
          description: '',
        },
  });

  const selectedClass = form.watch('account_class');
  const { data: accounts = [] } = useChartOfAccounts(company?.id || '', {
    accountClass: selectedClass as AccountClass,
  });

  const parentOptions = accounts.filter(
    (acc) => acc.id !== account?.id && !acc.parent_code
  );

  const onSubmit = async (data: AccountFormData) => {
    try {
      if (account) {
        await updateAccount.mutateAsync({
          id: account.id,
          account_name: data.account_name,
          account_name_ar: data.account_name_ar || null,
          description: data.description || null,
        });
        toast.success('Compte modifié avec succès / تم تعديل الحساب بنجاح');
      } else {
        await createAccount.mutateAsync({
          ...data,
          company_id: company!.id,
          parent_code: data.parent_code && data.parent_code !== 'none' ? data.parent_code : null,
          nature: data.nature || null,
          account_name_ar: data.account_name_ar || null,
          description: data.description || null,
        });
        toast.success('Compte créé avec succès / تم إنشاء الحساب بنجاح');
      }
      onClose();
      form.reset();
    } catch (error: any) {
      toast.error(error.message || 'Erreur lors de la sauvegarde');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {account
              ? 'تعديل الحساب / Modifier le compte'
              : 'حساب جديد / Nouveau compte'}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="account_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Code compte / رمز الحساب *</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        disabled={!!account}
                        placeholder="411"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="account_class"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Classe / الفئة *</FormLabel>
                    <Select
                      disabled={!!account}
                      onValueChange={field.onChange}
                      value={field.value?.toString()}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {ACCOUNT_CLASSES.map((cls) => (
                          <SelectItem key={cls.value} value={cls.value.toString()}>
                            {cls.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="account_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nom du compte (FR) / اسم الحساب *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Clients" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="account_name_ar"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nom du compte (AR) / الاسم بالعربية</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="العملاء" dir="rtl" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="account_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type / النوع *</FormLabel>
                    <Select
                      disabled={!!account}
                      onValueChange={field.onChange}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {ACCOUNT_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="nature"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nature / الطبيعة</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {NATURES.map((nature) => (
                          <SelectItem key={nature.value} value={nature.value}>
                            {nature.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {!account && (
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="parent_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Compte parent / الحساب الأب</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || 'none'}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Aucun" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">Aucun parent</SelectItem>
                          {parentOptions.map((acc) => (
                            <SelectItem key={acc.id} value={acc.account_code}>
                              {acc.account_code} - {acc.account_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="opening_balance"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Solde d'ouverture / الرصيد الافتتاحي</FormLabel>
                      <FormControl>
                        <Input type="number" step="0.01" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description / الوصف</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={3} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Annuler / إلغاء
              </Button>
              <Button
                type="submit"
                disabled={createAccount.isPending || updateAccount.isPending}
              >
                {(createAccount.isPending || updateAccount.isPending) && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                {account ? 'Modifier / تعديل' : 'Créer / إنشاء'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
